import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Accès au paiement Mobile Money, commerce par commerce.
//
// Le SuperAdmin peut autoriser un commerce précis à encaisser les paiements
// de ses clients (Mobile Money / carte) même quand la portée globale des
// paiements est limitée aux abonnements Zifek.
// ─────────────────────────────────────────────────────────────

export interface MerchantPaymentAccess {
  commerceId: number;
  enabled: boolean;
  commissionPercent: number | null;
  note: string | null;
  /** Moyens de paiement choisis par le commerce (feexpay, paypal…) */
  methods: string[];
  updatedAt: string | null;
}

export interface MerchantAccessRow extends MerchantPaymentAccess {
  name: string;
  userName: string;
  email: string;
  currency: string;
  photo: string | null;
}

function mapRow(row: Record<string, unknown>): MerchantPaymentAccess {
  let methods: string[] = [];
  try {
    const raw = row.payment_methods;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) methods = parsed.map((m) => String(m)).filter(Boolean);
  } catch {
    methods = [];
  }
  return {
    commerceId: Number(row.commerce_id),
    enabled: row.enabled !== false,
    commissionPercent: row.commission_percent != null ? Number(row.commission_percent) : null,
    note: (row.note as string) || null,
    methods,
    updatedAt: (row.updated_at as string) || null,
  };
}

/** État d'accès d'un commerce donné (null s'il n'a jamais été configuré). */
export async function getMerchantPaymentAccess(commerceId: number): Promise<MerchantPaymentAccess | null> {
  if (!commerceId) return null;
  try {
    const { data, error } = await supabase
      .from('merchant_payment_access')
      .select('*')
      .eq('commerce_id', commerceId)
      .maybeSingle();
    if (error) return null;
    return data ? mapRow(data as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Liste des commerces avec leur état d'accès.
 * La recherche porte sur le nom du commerce, le pseudo ou l'email.
 */
export async function listMerchantPaymentAccess(search = '', limit = 60): Promise<MerchantAccessRow[]> {
  const q = search.trim();

  let usersQuery = supabase
    .from('users')
    .select('id, name, user_name, email, monaie, image')
    .order('id', { ascending: false })
    .limit(limit);

  if (q) {
    usersQuery = usersQuery.or(`name.ilike.%${q}%,user_name.ilike.%${q}%,email.ilike.%${q}%`);
  }

  const { data: usersData, error: usersError } = await usersQuery;
  if (usersError) throw usersError;

  const users = (usersData || []) as {
    id: number;
    name: string | null;
    user_name: string | null;
    email: string | null;
    monaie: string | null;
    image: string | null;
  }[];
  if (users.length === 0) return [];

  const ids = users.map((u) => Number(u.id)).filter(Boolean);
  const { data: accessData } = await supabase
    .from('merchant_payment_access')
    .select('*')
    .in('commerce_id', ids);

  const map = new Map<number, MerchantPaymentAccess>();
  ((accessData || []) as Record<string, unknown>[]).forEach((row) => {
    const parsed = mapRow(row);
    map.set(parsed.commerceId, parsed);
  });

  return users.map((u) => {
    const access = map.get(Number(u.id));
    return {
      commerceId: Number(u.id),
      enabled: access?.enabled ?? false,
      commissionPercent: access?.commissionPercent ?? null,
      note: access?.note ?? null,
      methods: access?.methods ?? [],
      updatedAt: access?.updatedAt ?? null,
      name: u.name || u.user_name || `Commerce #${u.id}`,
      userName: u.user_name || '',
      email: u.email || '',
      currency: u.monaie || 'MAD',
      photo: u.image || null,
    };
  });
}

/** Autorise ou retire l'accès au paiement en ligne d'un commerce. */
export async function setMerchantPaymentAccess(params: {
  commerceId: number;
  enabled: boolean;
  commissionPercent?: number | null;
  note?: string | null;
  methods?: string[];
  updatedBy?: number | null;
}): Promise<void> {
  const { error } = await supabase.from('merchant_payment_access').upsert(
    {
      commerce_id: params.commerceId,
      enabled: params.enabled,
      commission_percent: params.commissionPercent ?? null,
      note: params.note ?? null,
      ...(params.methods ? { payment_methods: params.methods } : {}),
      updated_by: params.updatedBy ?? null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'commerce_id' },
  );
  if (error) throw error;
}

/**
 * Enregistre les moyens de paiement choisis par le commerce.
 * Seuls les moyens réellement activés par le SuperAdmin peuvent être retenus :
 * on filtre ici pour empêcher qu'un moyen désactivé soit imposé au checkout.
 */
export async function setMerchantPaymentMethods(params: {
  commerceId: number;
  methods: string[];
  allowed: string[];
  enabled?: boolean;
}): Promise<string[]> {
  const allowed = new Set(params.allowed);
  const methods = params.methods.filter((m) => allowed.has(m));
  const { error } = await supabase.from('merchant_payment_access').upsert(
    {
      commerce_id: params.commerceId,
      enabled: params.enabled ?? true,
      payment_methods: methods,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'commerce_id' },
  );
  if (error) throw error;
  return methods;
}